import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Bot,
  Send,
  Loader2,
  Wand2,
  AlertCircle,
  ArrowUpRight,
} from 'lucide-react';
import { getApiBase } from '../apiBase';
import { selectSnapshotRulesForTask, listSnapshotScopes } from './approvalSnapshotSelect';

const API = axios.create({ baseURL: getApiBase(), withCredentials: true, timeout: 90000 });

const INTENT_CHIPS = [
  { id: '', label: 'Auto' },
  { id: 'explain', label: 'Explain' },
  { id: 'recommend', label: 'Recommend' },
  { id: 'diff', label: 'Diff vs live' },
  { id: 'author', label: 'Create / Edit' },
];

const QUICK_PROMPTS = [
  { intent: 'explain', text: 'Explain these rules in plain English and flag any risks.' },
  { intent: 'recommend', text: 'Any duplicate, conflicting, or missing rules I should fix?' },
  { intent: 'author', text: 'Create a rule: route over 10% to the 2nd-level manager, else auto-approve.' },
];

/** Minimal markdown-ish renderer: **bold**, bullet lines, and paragraphs. */
function RichText({ text }) {
  const lines = String(text || '').split('\n');
  const renderInline = (s) => {
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      /^\*\*[^*]+\*\*$/.test(p) ? (
        <strong key={i} className="font-semibold text-slate-900">
          {p.slice(2, -2)}
        </strong>
      ) : (
        <span key={i}>{p}</span>
      )
    );
  };
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={i} className="h-1" />;
        if (/^[-*]\s+/.test(trimmed)) {
          return (
            <div key={i} className="flex gap-1.5 pl-1">
              <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-indigo-400" />
              <span className="flex-1">{renderInline(trimmed.replace(/^[-*]\s+/, ''))}</span>
            </div>
          );
        }
        return <p key={i}>{renderInline(trimmed)}</p>;
      })}
    </div>
  );
}

/**
 * Conversational Approval Copilot scoped to one task.
 *
 * @param {object}   props.task            { name, taskKey, code, category }
 * @param {object}   [props.currentDesign] draft currently in the editor
 * @param {object[]} [props.existingRules] override; defaults to snapshot rules for the task
 * @param {string}   [props.transactionCategory]
 * @param {(design:object)=>void} props.onApplyDesign  seed the editor with a suggested rule
 * @param {boolean} [props.showScopePicker]  when true (no fixed task), let the user pick a rule scope
 */
export default function ApprovalCopilotChat({
  task,
  currentDesign,
  existingRules,
  transactionCategory,
  onApplyDesign,
  showScopePicker = false,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [intent, setIntent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devWarning, setDevWarning] = useState('');
  const [scopeId, setScopeId] = useState('');
  const conversationIdRef = useRef(null);
  const scrollRef = useRef(null);

  const scopes = useMemo(() => (showScopePicker ? listSnapshotScopes() : []), [showScopePicker]);

  // The task the chat is grounded on: an explicit task prop, or the picked scope.
  const effectiveTask = useMemo(() => {
    if (task) return task;
    if (showScopePicker && scopeId) {
      const s = scopes.find((sc) => sc.id === scopeId);
      if (s) return { name: s.name, taskKey: s.taskKey, category: s.category, transaction: s.category };
    }
    return null;
  }, [task, showScopePicker, scopeId, scopes]);

  const groundedRules = useMemo(() => {
    if (Array.isArray(existingRules) && existingRules.length) return existingRules;
    if (!effectiveTask) return [];
    return selectSnapshotRulesForTask({
      taskKey: effectiveTask.taskKey,
      name: effectiveTask.name,
      category: effectiveTask.category || transactionCategory,
    }).rules;
  }, [existingRules, effectiveTask, transactionCategory]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // Reset the conversation when the grounded task/scope changes.
  useEffect(() => {
    setMessages([]);
    setError('');
    setDevWarning('');
    conversationIdRef.current = null;
  }, [effectiveTask?.taskKey, effectiveTask?.name]);

  async function sendMessage(text, intentOverride) {
    const content = String(text || '').trim();
    if (!content || loading) return;
    const useIntent = intentOverride != null ? intentOverride : intent;
    const next = [...messages, { role: 'user', content }];
    setMessages(next);
    setInput('');
    setLoading(true);
    setError('');

    try {
      const { data } = await API.post('/api/copilot/approvals/chat', {
        messages: next.map(({ role, content: c }) => ({ role, content: c })),
        intent: useIntent || undefined,
        task: effectiveTask
          ? {
              name: effectiveTask.name,
              taskKey: effectiveTask.taskKey,
              transaction: effectiveTask.transaction || transactionCategory || effectiveTask.category,
            }
          : undefined,
        currentDesign:
          currentDesign && Array.isArray(currentDesign.stages) && currentDesign.stages.length
            ? currentDesign
            : undefined,
        existingRules: groundedRules,
        transactionCategory: transactionCategory || effectiveTask?.category,
        conversationId: conversationIdRef.current || undefined,
        turnIndex: next.filter((m) => m.role === 'user').length - 1,
      });
      if (data.conversationId) conversationIdRef.current = data.conversationId;
      if (data.devContextWarning) setDevWarning(data.devContextWarning);
      setMessages((cur) => [
        ...cur,
        {
          role: 'assistant',
          content: data.reply || '(no reply)',
          intent: data.intent,
          suggestedDesign: data.suggestedDesign || null,
          warnings: Array.isArray(data.warnings) ? data.warnings : [],
        },
      ]);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Approval Copilot request failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="flex min-h-0 flex-col rounded-xl border border-indigo-200 bg-white shadow-sm">
      <header className="flex items-center gap-2 border-b border-indigo-100 bg-gradient-to-r from-indigo-50 to-violet-50 px-4 py-2.5">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 text-white">
          <Bot className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-900">Approval Copilot</h3>
          <p className="truncate text-[11px] text-slate-500">
            {effectiveTask?.name
              ? `Grounded on “${effectiveTask.name}”`
              : 'Ask anything about approvals, or pick a rule set to ground answers'}
            {groundedRules.length ? ` · ${groundedRules.length} rules in context` : ''}
          </p>
        </div>
        {showScopePicker && (
          <select
            value={scopeId}
            onChange={(e) => setScopeId(e.target.value)}
            className="shrink-0 rounded-lg border border-indigo-200 bg-white px-2 py-1 text-[11px] text-slate-700 focus:border-indigo-400 focus:outline-none"
            title="Ground the Copilot on a rule set"
          >
            <option value="">General (no task)</option>
            {scopes.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        )}
      </header>

      {/* Intent selector */}
      <div className="flex flex-wrap gap-1.5 border-b border-slate-100 px-3 py-2">
        {INTENT_CHIPS.map((chip) => (
          <button
            key={chip.id || 'auto'}
            type="button"
            onClick={() => setIntent(chip.id)}
            className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
              intent === chip.id
                ? 'bg-indigo-600 text-white'
                : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-100'
            }`}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="min-h-[180px] flex-1 space-y-3 overflow-y-auto px-3 py-3">
        {messages.length === 0 && !loading && (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">
              I can explain the rules on this task, suggest improvements, compare your draft to what's
              live, or draft a new rule you can drop into the editor.
            </p>
            <div className="flex flex-col gap-1.5">
              {QUICK_PROMPTS.map((q) => (
                <button
                  key={q.text}
                  type="button"
                  onClick={() => sendMessage(q.text, q.intent)}
                  className="flex items-start gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-left text-[11px] text-slate-700 hover:border-indigo-300 hover:bg-indigo-50"
                >
                  <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-indigo-500" />
                  {q.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) =>
          m.role === 'user' ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-indigo-600 px-3 py-1.5 text-xs text-white">
                {m.content}
              </div>
            </div>
          ) : (
            <div key={i} className="flex justify-start">
              <div className="max-w-[92%] space-y-2 rounded-2xl rounded-bl-sm bg-slate-100 px-3 py-2 text-xs text-slate-800">
                {m.intent && (
                  <span className="inline-block rounded bg-slate-200 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                    {m.intent}
                  </span>
                )}
                <RichText text={m.content} />
                {m.suggestedDesign && (
                  <button
                    type="button"
                    onClick={() => onApplyDesign && onApplyDesign(m.suggestedDesign)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:from-indigo-600 hover:to-violet-700"
                  >
                    <Wand2 className="h-3.5 w-3.5" />
                    Apply to editor
                  </button>
                )}
              </div>
            </div>
          )
        )}

        {loading && (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Thinking…
          </div>
        )}
      </div>

      {(error || devWarning) && (
        <div className="space-y-1 px-3 pb-1">
          {error && (
            <div className="flex items-start gap-1.5 rounded-lg bg-rose-50 px-2.5 py-1.5 text-[11px] text-rose-800">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {devWarning && !error && (
            <p className="px-1 text-[10px] text-amber-700">{devWarning}</p>
          )}
        </div>
      )}

      {/* Composer */}
      <div className="flex items-end gap-2 border-t border-slate-100 p-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage(input);
            }
          }}
          rows={1}
          placeholder="Ask the Approval Copilot…"
          disabled={loading}
          className="max-h-28 min-h-[36px] flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-800 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 disabled:bg-slate-50"
        />
        <button
          type="button"
          onClick={() => sendMessage(input)}
          disabled={!input.trim() || loading}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-300"
          aria-label="Send"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
    </section>
  );
}
